<template>
  <div class="contact">
    <AppNavbar active-tab="Contact" theme="light" />

    <section class="contact-main">
      <div class="contact-inner">
        <div class="contact-left">
          <h2 class="contact-subtitle">Contact Us</h2>
          <h1 class="contact-title">
            We are so happy that you want to get in touch!
          </h1>
        </div>
        <div class="contact-right">
          <form class="contact-form" novalidate @submit.prevent="handleSubmit">
            <div class="form-row">
              <input type="text" v-model="form.name" placeholder="Name" class="form-input" />
              <input type="email" v-model="form.email" placeholder="E-mail" class="form-input" />
            </div>
            <textarea
              v-model="form.message"
              placeholder="Message"
              class="form-textarea"
              rows="5"
            ></textarea>
            <button type="submit" class="btn-notify" :disabled="submitting">
              <img src="@/assets/iBuzzPen/email-icon.png" alt="" class="btn-icon" />
              {{ submitting ? 'Submitting...' : 'Notify Us' }}
            </button>
            <p v-if="submitTip" class="form-tip" :class="{ ok: submitOk }">{{ submitTip }}</p>
          </form>
        </div>
      </div>
    </section>

    <AppFooter horizontal-padding="clamp(20px, 8.44vw, 162px)" />
  </div>
</template>

<script>
import axios from 'axios'
import AppNavbar from '@/components/AppNavbar.vue'
import AppFooter from '@/components/AppFooter.vue'

export default {
  name: 'Contact',
  components: { AppNavbar, AppFooter },
  data() {
    return {
      form: {
        name: '',
        email: '',
        message: ''
      },
      submitting: false,
      submitTip: '',
      submitOk: false
    }
  },
  methods: {
    async handleSubmit() {
      if (this.submitting) return
      if (!this.form.name || !this.form.email || !this.form.message) {
        this.submitOk = false
        this.submitTip = 'Please fill in all fields.'
        return
      }
      if (!/^[a-zA-Z0-9_\-+.]+@[a-zA-Z0-9\-]+(\.[a-zA-Z0-9\-]+)+$/.test(this.form.email)) {
        this.submitOk = false
        this.submitTip = 'Invalid email format.'
        return
      }
      this.submitting = true
      this.submitTip = ''

      const params = {
        joinName: this.form.name,
        joinMsg: this.form.message,
        joinMail: this.form.email,
        joinType: '0'
      }
      try {
        await axios.post(`${this.BaseUrl}/api/ana/submitFeedback`, params)
        this.submitOk = true
        this.submitTip = 'Submitted successfully. Thank you!'
        this.form = { name: '', email: '', message: '' }
      } catch (e) {
        this.submitOk = false
        this.submitTip = 'Submission failed, please try again later.'
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
$container-max: 1760px;
$container-padding: clamp(24px, 10.94vw, 210px);

.contact {
  width: 100%;
  min-height: 100vh;
  background: #fff;
}

// ========== Main Contact Section ==========
.contact-main {
  padding: 140px $container-padding 80px;
  background: #fff;

  .contact-inner {
    max-width: $container-max;
    margin: 0 auto;
    display: flex;
    gap: 60px;
    align-items: flex-start;
  }

  .contact-left {
    flex: 1;
    padding-top: 20px;
  }

  .contact-subtitle {
    font-size: clamp(20px, 1.6vw, 26px);
    font-weight: bold;
    color: #333;
    margin: 0 0 16px;
  }

  .contact-title {
    font-size: clamp(30px, 2.6vw, 48px);
    font-weight: bold;
    color: #1a1a1a;
    line-height: 1.3;
    margin: 0;
    letter-spacing: -0.5px;
  }

  .contact-right {
    flex: 0 0 52%;
    background: #F3F3F3;
    border-radius: 16px;
    padding: 38px 36px;
    box-sizing: border-box;
  }
}

// ========== Form ==========
.contact-form {
  .form-row {
    display: flex;
    gap: 16px;
    margin-bottom: 16px;
  }

  .form-input {
    flex: 1;
    padding: 14px 16px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    font-size: 15px;
    background: #fff;
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.2s;

    &:focus { border-color: #999; }
    &::placeholder { color: #999; }
  }

  .form-textarea {
    width: 100%;
    padding: 14px 16px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    font-size: 15px;
    background: #fff;
    outline: none;
    resize: vertical;
    box-sizing: border-box;
    font-family: inherit;
    margin-bottom: 20px;
    transition: border-color 0.2s;

    &:focus { border-color: #999; }
    &::placeholder { color: #999; }
  }

  .btn-notify {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 12px 30px;
    border: none;
    border-radius: 35px;
    background: linear-gradient( 180deg, #06030B 0%, #34264C 100%);
    color: #fff;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.3s, transform 0.2s;

    .btn-icon {
      width: 28px;
      height: 28px;
      filter: brightness(0) invert(1);
    }

    &:hover {
      opacity: 0.9;
      transform: translateY(-1px);
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  .form-tip {
    margin: 16px 0 0;
    font-size: 14px;
    color: #e6435f;

    &.ok { color: #10aa32; }
  }
}

// ========== Responsive: Tablet ==========
@media (max-width: 1024px) {
  .contact-main {
    .contact-inner {
      flex-direction: column;
      gap: 40px;
    }
    .contact-right {
      flex: 0 0 100%;
      width: 100%;
    }
  }
}

// ========== Responsive: Mobile ==========
@media (max-width: 768px) {
  .contact-main {
    padding: 100px 20px 60px;

    .contact-right {
      padding: 24px 20px;
    }
  }

  .contact-form {
    .form-row {
      flex-direction: column;
      gap: 12px;
    }
  }
}
</style>
